import React, { useEffect, useMemo, useState } from 'react';
import FfmpegPresetWizard from './FfmpegPresetWizard';

/**
 * Panel de administración de presets FFmpeg (CRUD).
 * Requiere props:
 *  - api: { listPresets, createPreset, updatePreset, deletePreset }
 *  - onClose: () => void
 *  - setMessage: (msg: string) => void
 */
export default function FfmpegPresetsPanel({ api, onClose, setMessage }) {
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [filterRole, setFilterRole] = useState('all'); // all|video|audio|any
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // preset or null
  const [form, setForm] = useState({ name:'', role:'any', command:'', description:'', tags:'', isDefault:false });
  const [showWizard, setShowWizard] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const response = await api.listPresets();
      // Backend returns { data: { success: true, presets: [...] } }
      const presetsList = response?.data?.presets || [];
      setPresets(Array.isArray(presetsList) ? presetsList : []);
    } catch (e) {
      setMessage?.(`Error loading presets: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let arr = presets;
    if (filterRole !== 'all') arr = arr.filter(p => (p.role || 'any') === filterRole);
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(p => {
        const tagsStr = Array.isArray(p.tags) ? p.tags.join(' ') : (p.tags || '');
        return (p.name || '').toLowerCase().includes(q) ||
               (p.description || '').toLowerCase().includes(q) ||
               tagsStr.toLowerCase().includes(q);
      });
    }
    return arr;
  }, [presets, filterRole, query]);

  const resetForm = () => {
    setEditing(null);
    setForm({ name:'', role:'any', command:'', description:'', tags:'', isDefault:false });
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      role: p.role || 'any',
      command: p.command || '',
      description: p.description || '',
      tags: Array.isArray(p.tags) ? p.tags.join(',') : (p.tags || ''),
      isDefault: !!p.isDefault
    });
  };

  const onSave = async () => {
    const payload = {
      name: (form.name || '').trim(),
      role: form.role || 'any',
      command: (form.command || '').trim(),
      description: form.description || '',
      tags: (form.tags || '').split(',').map(s => s.trim()).filter(Boolean),
      isDefault: !!form.isDefault
    };
    if (!payload.name || !payload.command) {
      setMessage?.('Name and command are required');
      return;
    }
    setLoading(true);
    try {
      if (editing?._id) {
        await api.updatePreset(editing._id, payload);
        setMessage?.('Preset updated');
      } else {
        await api.createPreset(payload);
        setMessage?.('Preset created');
      }
      resetForm();
      await fetchAll();
    } catch (e) {
      setMessage?.(`Save preset error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = async (presetData) => {
    setLoading(true);
    try {
      await api.createPreset(presetData);
      setMessage?.('Preset created via wizard');
      setShowWizard(false);
      await fetchAll();
    } catch (e) {
      setMessage?.(`Wizard save error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (p) => {
    const ok = window.confirm(`Delete preset "${p.name}"?`);
    if (!ok) return;
    setLoading(true);
    try {
      await api.deletePreset(p._id);
      setMessage?.('Preset deleted');
      await fetchAll();
    } catch (e) {
      setMessage?.(`Delete preset error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const pill = (bg) => ({ display:'inline-block', padding:'3px 8px', borderRadius:12, color:'#fff', background:bg, fontSize:12 });
  const btn = (bg) => ({ background:bg, color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', cursor:'pointer', boxShadow:'0 2px 8px rgba(154,81,226,0.15)', transition:'all 0.2s' });

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
    }}>
      <div style={{ width:'min(1100px, 96vw)', maxHeight:'90vh', overflow:'auto', background:'#23243a', color:'#fff', borderRadius:16, padding:24, boxShadow:'0 4px 32px #000' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <h3 style={{ margin:0 }}>FFmpeg Presets</h3>
          <span style={{ marginLeft:'auto' }} />
          <button onClick={onClose} style={btn('#9e9e9e')}>Close</button>
        </div>

        <div style={{ marginTop:12, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input
            placeholder="Search name/desc/tags…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ flex:'1 1 280px', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }}
          />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }}>
            <option value="all">All roles</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="any">Any</option>
          </select>
          <button onClick={() => setShowWizard(true)} style={{...btn('#9a51e2'), background:'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)'}} disabled={loading}>Use Wizard</button>
          <button onClick={fetchAll} style={btn('#607d8b')} disabled={loading}>Refresh</button>
        </div>

        <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:16 }}>
          <div style={{ minWidth:0, overflow:'hidden' }}>
            <h4 style={{ margin:'8px 0', color:'#fff' }}>{editing ? 'Edit preset' : 'New preset'}</h4>
            <div style={{ display:'grid', gap:8 }}>
              <div>
                <label style={{ fontSize:12, color:'#9a51e2', fontWeight:500 }}>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ width:'100%', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#9a51e2', fontWeight:500 }}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width:'100%', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }}>
                  <option value="video">video</option>
                  <option value="audio">audio</option>
                  <option value="any">any</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, color:'#9a51e2', fontWeight:500 }}>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ width:'100%', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#9a51e2', fontWeight:500 }}>Tags (comma separated)</label>
                <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} style={{ width:'100%', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px' }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#9a51e2', fontWeight:500 }}>FFmpeg Command</label>
                <textarea rows={8} value={form.command} onChange={e => setForm({ ...form, command: e.target.value })} style={{ width:'100%', fontFamily:'monospace', background:'#181828', color:'#fff', border:'1px solid #555', borderRadius:6, padding:'8px 12px', resize:'vertical' }} />
              </div>
              <label style={{ display:'flex', gap:8, alignItems:'center', color:'#fff' }}>
                <input type="checkbox" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
                Set as default for role
              </label>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={onSave} disabled={loading} style={{...btn('#4caf50'), background:'linear-gradient(90deg, #4caf50 60%, #388e3c 100%)'}}>{editing ? 'Update' : 'Create'}</button>
                {editing && <button onClick={resetForm} style={btn('#9e9e9e')}>Cancel</button>}
              </div>
            </div>
          </div>

          <div style={{ minWidth:0, overflow:'hidden' }}>
            <h4 style={{ margin:'8px 0', color:'#fff' }}>Presets</h4>
            <div style={{ border:'1px solid #555', borderRadius:8, maxHeight: '58vh', overflow:'auto', background:'#181828' }}>
              {loading && <div style={{ padding:12, color:'#9a51e2' }}>Loading…</div>}
              {!loading && filtered.map(p => (
                <div key={p._id} style={{ padding:12, borderBottom:'1px solid #333' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <b style={{ color:'#fff' }}>{p.name}</b>
                    <span style={pill(p.role === 'audio' ? '#00bcd4' : p.role === 'video' ? '#3f51b5' : '#607d8b')}>
                      {p.role || 'any'}
                    </span>
                    {p.isDefault && <span style={pill('#4caf50')}>default</span>}
                    <span style={{ marginLeft:'auto' }} />
                    <button onClick={() => startEdit(p)} style={{...btn('#607d8b'), padding:'6px 12px', fontSize:14}}>Edit</button>
                    <button onClick={() => onDelete(p)} style={{...btn('#e53935'), padding:'6px 12px', fontSize:14}}>Delete</button>
                  </div>
                  {p.description && <div style={{ fontSize:12, color:'#aaa', marginTop:4 }}>{p.description}</div>}
                  {p.tags && Array.isArray(p.tags) && p.tags.length > 0 && (
                    <div style={{ marginTop:6, display:'flex', gap:6, flexWrap:'wrap' }}>
                      {p.tags.map(t => <span key={t} style={pill('#9e9e9e')}>{t}</span>)}
                    </div>
                  )}
                  <pre style={{ marginTop:8, background:'#0d0d15', color:'#d4d4d4', padding:8, borderRadius:6, border:'1px solid #333', whiteSpace:'pre-wrap', fontFamily:'monospace', fontSize:12 }}>
                    {p.command}
                  </pre>
                </div>
              ))}
              {!loading && filtered.length === 0 && (
                <div style={{ padding:12, color:'#9a51e2' }}>No presets</div>
              )}
            </div>
          </div>
        </div>

      </div>

      {showWizard && (
        <FfmpegPresetWizard
          onComplete={handleWizardComplete}
          onCancel={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
